import React, { useMemo } from 'react';
import { select } from '~/lib/select';
import { Chapter, TrainingState, useTrainingStore } from '../state';
import { Bar, Container } from './ProgressBar.styles';
import { t } from 'ttag';

const getIndex = (chapter: Chapter, position: number) =>
  Math.max(0, Math.min(chapter.sequenceCount, position - chapter.position));

const selector = (s: TrainingState) => select(s, 'items', 'activeItemPosition');

/** CAT-1239 / Figma: greyDark border (#6f6f6f) for non-text contrast ≥ 3:1 */
const progressBorder = '1px solid';
const progressBorderColor = 'greyDark';

export const ProgressBar = () => {
  const { items, activeItemPosition } = useTrainingStore(selector);
  const segments = useMemo(
    () =>
      items
        .filter(i => i.kind === 'chapter')
        .map((c: Chapter) => ({
          id: c.id,
          length: c.sequenceCount,
          index: getIndex(c, activeItemPosition)
        })),
    [items, activeItemPosition]
  );

  const percentage = useMemo(() => {
    let lengthSum = 0;
    let indexSum = 0;
    segments.forEach(segment => {
      lengthSum += segment.length;
      indexSum += segment.index;
    });
    if (!lengthSum) return 0;
    return Math.round((indexSum * 100) / lengthSum);
  }, [segments]);

  if (activeItemPosition < 0) return null;

  const valueText = `${percentage}% ${t`of the course passed`}`;

  return (
    <Container
      role="progressbar"
      aria-label={t`Course progress`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percentage}
      aria-valuetext={valueText}
    >
      {segments.map(s => (
        <Bar aria-hidden="true" key={s.id} flex={1}>
          <Bar
            // Figma 809:3219 — translucent fill, solid greyDark border (not faded by opacity)
            bg="rgba(255, 255, 255, 0.4)"
            border={progressBorder}
            borderColor={progressBorderColor}
          />
          {s.index ? (
            <Bar
              // Figma 809:3223
              bg="#FFF"
              border={progressBorder}
              borderColor={progressBorderColor}
              w={`${(s.index * 100) / s.length}%`}
              // Overlay the full track (4px fill + 1px border × 2 ≈ 6px)
              marginTop="-6px"
            />
          ) : null}
        </Bar>
      ))}
    </Container>
  );
};
